import { DocumentSigningRoleType } from '@dealstack/shared/lib/generated';
import type {
	PendingSlot,
	SettingsForSlots,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentModal/placement/clientSideSlots.types';

const buildKey = (
	roleType: DocumentSigningRoleType,
	roleName: string,
	signatureIndex: number,
	isWitness: boolean
): string => `${roleType}:${roleName}:${signatureIndex}:${isWitness ? 'w' : 'p'}`;

/**
 * Enumerate editor slots at send time from form state (no template yet). Must
 * mirror the backend `enumerateSlots` or placements fail with INVALID_PLACEMENTS.
 * PARTY: Investor (idx 0) + Representative (idx 1, PIV). COUNTERPARTY: one per
 * (counterparty, signature). Every slot is paired with a witness slot unconditionally,
 * matching the backend — witness config governs who signs, not whether a box exists.
 */
export function enumerateClientSlots(settings: SettingsForSlots): PendingSlot[] {
	const slots: PendingSlot[] = [];

	const pushPair = (
		roleType: DocumentSigningRoleType,
		roleName: string,
		signatureIndex: number,
		label: string
	): void => {
		slots.push({
			key: buildKey(roleType, roleName, signatureIndex, false),
			roleType,
			roleName,
			signatureIndex,
			isWitness: false,
			label,
		});
		slots.push({
			key: buildKey(roleType, roleName, signatureIndex, true),
			roleType,
			roleName,
			signatureIndex,
			isWitness: true,
			label: `${label} — Witness`,
		});
	};

	// PARTY: Investor #1 (index 0) + Representative #1 (index 1), matching the
	// backend enumerator's partyN=1 path.
	pushPair(DocumentSigningRoleType.PARTY, '', 0, 'Investor #1');
	pushPair(DocumentSigningRoleType.PARTY, '', 1, 'Representative #1');

	for (const counterparty of settings.counterparties) {
		for (let i = 0; i < counterparty.amount; i++) {
			pushPair(
				DocumentSigningRoleType.COUNTERPARTY,
				counterparty.name,
				i,
				`${counterparty.name} #${i + 1}`
			);
		}
	}

	return slots;
}
